import { LineItemNotesAccessMode } from '@domain/configuration/types';
import { useChatSalesPortalDynamicConfigValue } from '@/experiences/sales-portal/blocs/hooks/use-chat-dynamic-config';
import { useSalesPortalEmployeeRole } from '@/experiences/sales-portal/blocs/hooks/use-sales-portal-employee-role';

/**
 * Whether request notes are enabled and the current user can write them.
 *
 * Uses dynamic config `line_item_notes_config`:
 * - `enabled` must be true for the column to appear
 * - `access_mode`:
 *   - ALL: everyone can write (default)
 *   - DSR_ONLY: only users with an employee role can write
 *
 * @param supplierUUID - Supplier to check config and employee role for
 * @param chatUUID - Optional chat UUID passed to the dynamic config loader
 * @returns `true` when notes are enabled and the user has write access
 *
 * @example
 * ```ts
 * const canWrite = useCanWriteRequestNote(supplierUUID, chatUUID);
 * // canWrite === true when notes enabled + user has access
 * ```
 */
export const useCanWriteRequestNote = (
  supplierUUID: string,
  chatUUID?: string | null,
): boolean => {
  const { value: notesConfig } = useChatSalesPortalDynamicConfigValue(
    supplierUUID,
    'line_item_notes_config',
    chatUUID,
  );

  const { employeeRole } = useSalesPortalEmployeeRole(supplierUUID);

  if (!notesConfig?.enabled) {
    return false;
  }

  const accessMode = notesConfig.access_mode ?? LineItemNotesAccessMode.ALL;

  if (accessMode === LineItemNotesAccessMode.DSR_ONLY) {
    return employeeRole !== null;
  }

  return true;
};
